import React, { useState, useMemo } from 'react';
import { Check } from '@mui/icons-material';
import './ItemCard.css';
import './SearchCard.css';
import ItemView from './ItemView';
import StatsList from '../StatsList';
import { processItemRequirementText } from '../../core/statProcessing';
import { useOverlay } from '../../contexts/OverlayContext';
import { useGeneratorContext } from '../../contexts/GeneratorStateContext';
import ItemHeader from './ItemHeader';
import SearchService from '../../services/SearchService';
import SetView from './SetView';
import SetHeader from './SetHeader';
import OverlayEquipFooter from '../OverlayEquipFooter';
import { useEquipItem } from '../../hooks/useEquipItem';
import {
    buildElementGradient,
    buildElementGradientHover,
    buildElementIconFilter,
    buildElementIconFilterCompact,
    buildElementIconFilterSoft
} from '../utils/CardGradient';
import { WeaponDamageList, WeaponMetaList } from '../weapon/WeaponSummary';
import { hasWeaponDamages, buildWeaponMetaRows } from '../../core/weapon';
import { applyWeaponMagingPreview } from '../../core/weaponMaging';
import { magedStatRows } from '../../core/itemMaging';
import { weaponSheetFor } from '../../core/weaponDamage';
import Tooltip from '../utils/Tooltip';
import CardTopbar from './CardTopbar';
import {
    hasItemEffect,
    getItemEffectName,
    getItemEffectDescription,
    ITEM_EFFECT_ICON
} from '../../core/itemEffect';
import MediaImg from '../utils/MediaImg';
import CompactCardTile from './CompactCardTile';

const TXT_EQUIP = { fr: 'Équiper', en: 'Equip', de: 'Ausrüsten', es: 'Equipar', pt: 'Equipar' };
const TXT_EQUIPPED = { fr: 'Équipé', en: 'Equipped', de: 'Ausgerüstet', es: 'Equipado', pt: 'Equipado' };

const ItemCard = React.forwardRef(({ item, lang = 'fr', compact = false, dense = false, onItemClick, onCardClick, onTooltipShow, onTooltipHide, preferredSlotId = null, showWeaponDamage = false, showType = false, equipLabel = null, equipIcon = null, isEquipped = false, weaponMaging = null, itemMaging = null, onEquippedClick = null }, ref) => {
    const { showOverlay, hideOverlay } = useOverlay();
    const { equipItem, equipSet } = useEquipItem(lang);
    const { build, genConfig } = useGeneratorContext();
    const [isHovered, setIsHovered] = useState(false);

    // Process criteria if available (must be before early return)
    const criteriaProcessed = useMemo(() => {
        // The compact card shows no conditions, so skip the text pass entirely:
        // it runs once per card and the compact grid holds a lot more of them.
        if (!item || dense) return null;
        return processItemRequirementText(item, lang, build?.lvl);
    }, [item, lang, dense, build?.lvl]);

    if (!item) return null;

    // Safely access item properties with fallbacks
    const itemName = item.name && typeof item.name === 'object'
        ? (item.name[lang] || item.name.fr || item.name.en || '')
        : (item.name || '');
    const itemIcon = item.icon || '/img/stat_re_armes.png';
    const itemLevel = item.level || item.lvl || item.lvl_min || 0;

    // The pinned card is the slot's own item, so it reads as forgemaged there.
    const statRows = dense ? [] : magedStatRows(item, itemMaging, false);
    const rawWeapon = item.weapon || null;
    // Damages get the maging preview applied; meta (range, crit, cost) is untouched by maging.
    const weapon = rawWeapon && weaponMaging
        ? applyWeaponMagingPreview(rawWeapon, weaponMaging)
        : rawWeapon;
    const itemTypeName = item.type ? (item.type.name && typeof item.type.name === 'object' ? item.type.name[lang] : item.type.name) : '';
    const itemTypeIcon = item.type ? item.type.img : null;
    const setName = item.item_set_id && item.item_set_name
        ? (item.item_set_name[lang] || (typeof item.item_set_name === 'object' ? (item.item_set_name.fr || item.item_set_name.en) : item.item_set_name))
        : null;

    const hasEffect = hasItemEffect(item);
    const effectName = hasEffect ? getItemEffectName(item, lang) : '';
    const effectDescription = hasEffect ? getItemEffectDescription(item, lang) : '';
    const showEffectRow = hasEffect && !!effectName;

    const gradientBackground = buildElementGradient(item.effects_map || {});
    const gradientBackgroundHover = buildElementGradientHover(item.effects_map || {});

    const hasStats = Array.isArray(statRows) && statRows.length > 0;
    const hasWeaponDamage = !dense && rawWeapon && hasWeaponDamages(rawWeapon);
    const hasWeaponMeta = !dense && rawWeapon && buildWeaponMetaRows(rawWeapon, lang).length > 0;

    const statSections = [];

    if (hasWeaponDamage) {
        statSections.push({
            id: 'weapon-damage',
            node: (
                <WeaponDamageList
                    weapon={weapon}
                    lang={lang}
                    className="item-card-weapon-damages"
                    // What this weapon would do on the build being edited *with
                    // this weapon on it* — the current one unequipped, this one's
                    // own stats and set tier counted. Reading the build's sheet
                    // as-is would price every card with the equipped weapon's
                    // bonuses and none of its own. Off — the default, so the
                    // encyclopedia and every set overlay stay on it, and only
                    // the weapon slot's search opts in — the card falls back
                    // to the dice printed on the item.
                    stats={showWeaponDamage ? weaponSheetFor(build, item, genConfig?.position) : null}
                    lvl={build?.lvl ?? null}
                />
            )
        });
    }

    if (hasStats) {
        statSections.push({
            id: 'stats',
            node: (
                <div className="item-card-main-stats column">
                    <StatsList stats={statRows} className="item-card-stats-col" lang={lang} />
                    {showEffectRow && (
                        <>
                            <div className="item-card-divider item-card-legendary-divider" />
                            <div className="item-card-legendary-wrapper">
                                <Tooltip
                                    content={
                                        effectDescription
                                            ? (
                                                <div className="item-card-legendary-tooltip">
                                                    {effectDescription}
                                                </div>
                                            )
                                            : null
                                    }
                                >
                                    <div className="item-card-legendary-row">
                                        <img
                                            className="item-card-legendary-icon"
                                            src={ITEM_EFFECT_ICON}
                                            alt={effectName}
                                        />
                                        <span className="item-card-legendary-label">
                                            {effectName}
                                        </span>
                                    </div>
                                </Tooltip>
                            </div>
                        </>
                    )}
                </div>
            )
        });
    }

    if (hasWeaponMeta) {
        statSections.push({
            id: 'weapon-meta',
            node: (
                <WeaponMetaList
                    weapon={rawWeapon}
                    lang={lang}
                    className="item-card-weapon-meta"
                />
            )
        });
    }

    const handleEquipWithPreferredSlot = (itemToEquip) => {
        if (onItemClick) {
            onItemClick(itemToEquip);
            return;
        }
        equipItem(itemToEquip, preferredSlotId);
    };

    const openItemOverlay = () => {
        showOverlay(
            [
                <ItemHeader key={`item-hdr-${item.id}`} item={item} lang={lang} />,
                <ItemView key={`item-view-${item.id}`} item={item} lang={lang} compact={false} />,
            ],
            {
                footer: (
                    <OverlayEquipFooter
                        lang={lang}
                        onEquip={() => handleEquipWithPreferredSlot(item)}
                    />
                ),
            }
        );
    };

    const openSetOverlay = (set) => {
        showOverlay(
            [
                <SetHeader key={`set-hdr-${set.id || item.item_set_id}`} set={set} lang={lang} />,
                <SetView
                    key={`set-view-${set.id || item.item_set_id}`}
                    set={set}
                    lang={lang}
                    onItemClick={onItemClick || ((it) => equipItem(it, preferredSlotId))}
                />,
            ],
            {
                footer: (
                    <OverlayEquipFooter
                        lang={lang}
                        onEquip={() => equipSet(set, preferredSlotId)}
                    />
                ),
            }
        );
    };

    const handleCardClick = () => {
        if (onCardClick) {
            onCardClick(item);
            return;
        }
        if (preferredSlotId != null) {
            handleEquipWithPreferredSlot(item);
            hideOverlay();
            return;
        }
        openItemOverlay();
    };

    const handleEquipClick = (e) => {
        e.stopPropagation();
        handleEquipWithPreferredSlot(item);
    };

    const openSet = async () => {
        if (!item.item_set_id) return;
        try {
            const set = await SearchService.getSet(item.item_set_id, lang);
            openSetOverlay(set);
        } catch (error) {
            console.error("Failed to fetch set details:", error);
        }
    };

    const handleSetClick = (e) => {
        e.stopPropagation();
        openSet();
    };

    // Compact list: art only. A click on the tile acts, exactly as it does on
    // the detailed card — the blacklist picker's own action, or equipping into
    // the slot this search was opened for. Only where there is nothing to act on
    // (the encyclopedia) does it open the item overlay instead.
    if (dense) {
        // What a click on the tile does. Nothing to equip on the pinned tile —
        // it is the item already in the slot — so its click stays on the
        // overlay, which on touch is the only way to read it.
        const tileAction = isEquipped && !onCardClick ? (onEquippedClick || openItemOverlay) : handleCardClick;
        // The hover panel is the item's details, so clicking it opens them (with
        // the equip button in the overlay's footer) rather than equipping
        // straight away like the tile does. The blacklist picker keeps its own
        // action on both: that overlay's footer would equip, which is not what
        // picking an item there means.
        const panelAction = isEquipped || onCardClick ? tileAction : openItemOverlay;
        return (
            <CompactCardTile
                ref={ref}
                className={isEquipped ? 'item-card-equipped' : ''}
                info={(
                    <div className="item-card-info">
                        {itemTypeIcon && (
                            <h6 className="item-card-type">
                                <img src={itemTypeIcon} className="item-card-type-icon" alt={itemTypeName} />
                            </h6>
                        )}
                        {itemLevel > 0 && (
                            <h6 className="item-card-level">
                                <span className="item-card-level-value">{itemLevel}</span>
                            </h6>
                        )}
                    </div>
                )}
                equipLabel={isEquipped ? null : (equipLabel || (TXT_EQUIP[lang] || TXT_EQUIP.en))}
                equipIcon={equipIcon}
                onEquip={isEquipped ? null : handleEquipClick}
                equipClassName="item-card-equip-button"
                badge={isEquipped ? (
                    <span className="item-card-equipped-badge">
                        <Check className="item-card-equipped-icon" fontSize="inherit" />
                    </span>
                ) : null}
                art={(
                    <MediaImg
                        className="compact-card-icon"
                        src={itemIcon}
                        alt={itemName}
                        retry={Boolean(item.icon)}
                        fill
                    />
                )}
                name={itemName}
                gradient={gradientBackground}
                gradientHover={gradientBackgroundHover}
                iconFilter={buildElementIconFilterCompact(item.effects_map || {})}
                onClick={tileAction}
                onPointerEnter={onTooltipShow ? (e) => onTooltipShow(item, e, {
                    interactive: true,
                    onOpenItem: panelAction,
                    onOpenSet: item.item_set_id ? openSet : null,
                }) : undefined}
                onPointerLeave={onTooltipHide ? (e) => onTooltipHide(e) : undefined}
            />
        );
    }

    return (
        <div
            className={`item-card card panel column${isEquipped ? ' item-card-equipped' : ''}${isEquipped && onEquippedClick ? ' item-card-equipped--clickable' : ''}`}
            ref={ref}
            // The pinned card is the item already in the slot: nothing to equip,
            // so a click opens that slot's own overlay when the search has one.
            onClick={isEquipped ? (onEquippedClick || undefined) : handleCardClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div
                className="item-card-gradient"
                style={{
                    // The pinned card tints on hover like any other, as its compact tile does.
                    background: isHovered ? gradientBackgroundHover : gradientBackground,
                    '--item-element-filter-icon': buildElementIconFilter(item.effects_map || {}),
                    '--item-element-filter-soft': buildElementIconFilterSoft(item.effects_map || {})
                }}
            >
                <CardTopbar
                    info={(
                        <div className="item-card-info">
                            {itemTypeName && (
                                <h6 className="item-card-type">
                                    {itemTypeIcon && <img src={itemTypeIcon} className="item-card-type-icon" alt={itemTypeName} />}
                                    {(!compact || showType) && (
                                        <span className="item-card-type-text">{itemTypeName}</span>
                                    )}
                                </h6>
                            )}
                            {itemTypeName && itemLevel > 0 && (
                                <span className="card-info-dot" aria-hidden="true">•</span>
                            )}
                            {itemLevel > 0 && (
                                <h6 className="item-card-level">
                                    <span className="item-card-level-value">{itemLevel}</span>
                                </h6>
                            )}
                        </div>
                    )}
                    equipLabel={isEquipped ? null : (equipLabel || (TXT_EQUIP[lang] || TXT_EQUIP.en))}
                    equipIcon={equipIcon}
                    onEquip={isEquipped ? null : handleEquipClick}
                    equipClassName="item-card-equip-button"
                    badge={isEquipped ? (
                        <span className="item-card-equipped-badge">
                            <Check className="item-card-equipped-icon" fontSize="inherit" />
                            {TXT_EQUIPPED[lang] || TXT_EQUIPPED.en}
                        </span>
                    ) : null}
                />
                <div className="item-card-body">
                    <div className="item-card-header">
                        <div className="item-card-art">
                            <MediaImg className="item-card-icon" src={itemIcon} alt={itemName} />
                        </div>
                        <div className="item-card-header-main">
                            <h5 className="item-card-name">{itemName}</h5>
                            {setName && !compact && <button className="item-card-set-button" onClick={handleSetClick}>{setName}</button>}
                        </div>
                    </div>
                    <div className="item-card-content">
                        {statSections.map((section, index) => (
                            <React.Fragment key={section.id}>
                                {index > 0 && <div className="item-card-divider" />}
                                {section.node}
                            </React.Fragment>
                        ))}
                        {statSections.length > 0 && criteriaProcessed && String(criteriaProcessed).trim() && (
                            <div className="item-card-divider item-card-divider-section" />
                        )}
                        {criteriaProcessed && String(criteriaProcessed).trim() && (
                            <div className="item-card-conditions">
                                <div className='item-card-conditions-text'>{criteriaProcessed}</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div >
    );
});

export default React.memo(ItemCard);
